import React, { useEffect, useMemo, useState, useCallback } from "react";
import { attendanceViewApi, errorMessage } from "../adminApi";
import { useAdminToast } from "../components/Toast";
import { Button, Loading, ErrorState, PageHeader, Pagination } from "../components/ui";
import { Icon } from "../components/icons";
import AttendanceViewModal, { initials, useAttendanceDetail } from "../components/AttendanceDetailModal";
import { formatTime, attendanceFlagColor } from "../format";

/**
 * TAMPILAN KALENDER ABSENSI — baris = karyawan, kolom = tanggal.
 * Rentang: minggu ini (default) / bulan ini / rentang custom (maks 62 hari).
 * Klik chip absen → modal detail (sama dengan tampilan list).
 */

const PAGE_SIZE = 30;
const MAX_SPAN_DAYS = 62;
const DAY_MS = 86400000;

// ── Helper tanggal "YYYY-MM-DD" (aritmetika di UTC agar bebas pergeseran zona) ──
const toKey = (d) => d.toISOString().slice(0, 10);
const fromKey = (k) => new Date(`${k}T00:00:00Z`);
const addDays = (k, n) => toKey(new Date(fromKey(k).getTime() + n * DAY_MS));
const isWeekend = (k) => [0, 6].includes(fromKey(k).getUTCDay());
const diffDays = (a, b) => Math.round((fromKey(b) - fromKey(a)) / DAY_MS);
function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function weekRange(anchor) {
  const dow = (fromKey(anchor).getUTCDay() + 6) % 7; // Senin = 0
  const from = addDays(anchor, -dow);
  return { from, to: addDays(from, 6) };
}
// Periode bulanan absensi = tgl 25 bulan lalu s/d tgl 24 bulan ini (cut-off payroll).
// Contoh periode "September": 25 Agu – 24 Sep. Tanggal ≥ 25 masuk periode bulan berikutnya.
const PERIOD_START_DAY = 25;
function monthRange(anchor) {
  const d = fromKey(anchor);
  const endMonth = d.getUTCMonth() + (d.getUTCDate() >= PERIOD_START_DAY ? 1 : 0);
  const from = toKey(new Date(Date.UTC(d.getUTCFullYear(), endMonth - 1, PERIOD_START_DAY)));
  const to = toKey(new Date(Date.UTC(d.getUTCFullYear(), endMonth, PERIOD_START_DAY - 1)));
  return { from, to };
}
function shiftMonth(anchor, n) {
  const end = fromKey(monthRange(anchor).to); // geser berdasar akhir periode agar tak loncat/ulang
  return toKey(new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() + n, PERIOD_START_DAY - 1)));
}
const fmt = (k, opts) => fromKey(k).toLocaleDateString("id-ID", { timeZone: "UTC", ...opts });
function rangeLabel({ from, to }) {
  const sameYear = from.slice(0, 4) === to.slice(0, 4);
  const sameMonth = sameYear && from.slice(5, 7) === to.slice(5, 7);
  const left = fmt(from, sameMonth ? { day: "numeric" } : sameYear ? { day: "numeric", month: "short" } : { day: "numeric", month: "short", year: "numeric" });
  return `${left} – ${fmt(to, { day: "numeric", month: "short", year: "numeric" })}`;
}

// Label & warna chip per status (warna mengikuti palet badge admin).
const STATUS_LABEL = {
  NORMAL: "Hadir",
  LATE: "Telat",
  HALF_DAY: "Half Day",
  EARLY_OUT: "Pulang Awal",
  WFH: "WFH",
  MANUAL_EDIT: "Manual",
  FORGOT_CHECKIN: "Lupa In",
  FORGOT_CHECKOUT: "Lupa Out",
  DOUBLE_CHECKIN: "Double In",
  DOUBLE_CHECKOUT: "Double Out",
  REJECTED: "Ditolak",
};
const CHIP = {
  green: { bg: "bg-[#e6f7ef]", dot: "bg-[#3fae84]", text: "text-[#2f6e5c]" },
  yellow: { bg: "bg-[#fdf1d8]", dot: "bg-[#e6a93c]", text: "text-[#8a5a24]" },
  blue: { bg: "bg-[#e3eeff]", dot: "bg-[#5b8def]", text: "text-[#4a63a0]" },
  red: { bg: "bg-[#fde8ea]", dot: "bg-[#e0566b]", text: "text-[#8f4657]" },
  gray: { bg: "bg-slate-100", dot: "bg-slate-400", text: "text-slate-600" },
};

const pill = "h-10 rounded-full bg-white border border-slate-200/80 shadow-[0_1px_2px_rgba(16,24,40,0.04)]";

export default function AttendanceCalendarView({ toggle }) {
  const toast = useAdminToast();
  const [mode, setMode] = useState("week"); // week | month | custom
  const [anchor, setAnchor] = useState(todayKey());
  const [custom, setCustom] = useState(() => weekRange(todayKey())); // rentang custom yang TERPAKAI
  const [departments, setDepartments] = useState([]);
  // Filter diisi bebas dulu (draft), baru dipakai query saat tombol "Cari" diklik.
  const [draft, setDraft] = useState(() => ({ search: "", department: "", ...weekRange(todayKey()) }));
  const [applied, setApplied] = useState({ search: "", department: "", n: 0 });
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ employees: [], records: [], leaves: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { selected, detail, open: openDetail, close: closeDetail } = useAttendanceDetail();

  const range = mode === "week" ? weekRange(anchor) : mode === "month" ? monthRange(anchor) : custom;
  const span = diffDays(range.from, range.to);
  const rangeValid = span >= 0 && span <= MAX_SPAN_DAYS - 1;
  const days = useMemo(
    () => (rangeValid ? Array.from({ length: span + 1 }, (_, i) => addDays(range.from, i)) : []),
    [range.from, span, rangeValid]
  );

  useEffect(() => {
    attendanceViewApi.departments().then(setDepartments).catch(() => setDepartments([]));
  }, []);

  const load = useCallback(async () => {
    if (!rangeValid) return;
    setLoading(true);
    setError(null);
    try {
      const params = { date_from: range.from, date_to: range.to, limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (applied.department) params.department = applied.department;
      if (applied.search) params.search = applied.search;
      const res = await attendanceViewApi.calendar(params);
      setData({
        employees: res.employees || [],
        records: res.records || [],
        leaves: res.leaves || [],
        total: res.pagination?.total ?? (res.employees || []).length,
      });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, rangeValid, applied, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Indeks: "user|tanggal" → record[]; "user|tanggal" → cuti.
  const { recMap, leaveSet } = useMemo(() => {
    const recMap = new Map();
    for (const r of data.records) {
      const k = `${r.user_id}|${r.date_key}`;
      if (!recMap.has(k)) recMap.set(k, []);
      recMap.get(k).push(r);
    }
    const leaveSet = new Set();
    for (const l of data.leaves) {
      const from = l.date_from < range.from ? range.from : l.date_from;
      const to = l.date_to > range.to ? range.to : l.date_to;
      for (let d = from; d <= to; d = addDays(d, 1)) leaveSet.add(`${l.user_id}|${d}`);
    }
    return { recMap, leaveSet };
  }, [data, range.from, range.to]);

  const nav = (dir) => {
    if (mode === "week") setAnchor((a) => addDays(a, 7 * dir));
    else if (mode === "month") setAnchor((a) => shiftMonth(a, dir));
    else {
      const next = { from: addDays(custom.from, (span + 1) * dir), to: addDays(custom.to, (span + 1) * dir) };
      setCustom(next);
      setDraft((d) => ({ ...d, ...next }));
    }
    setPage(0);
  };
  const changeMode = (m) => {
    if (m === "custom") {
      // mulai dari rentang yang sedang tampil
      setCustom(range);
      setDraft((d) => ({ ...d, from: range.from, to: range.to }));
    } else setAnchor(todayKey());
    setMode(m);
    setPage(0);
  };
  const setDraftField = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  // Tombol "Cari": validasi rentang di sini (bukan saat memilih), lalu jalankan query.
  const apply = () => {
    if (mode === "custom") {
      if (!draft.from || !draft.to) return toast.error("Isi tanggal awal & akhir");
      const s = diffDays(draft.from, draft.to);
      if (s < 0) return toast.error("Tanggal awal harus sebelum tanggal akhir");
      if (s > MAX_SPAN_DAYS - 1) return toast.error(`Rentang maksimal ${MAX_SPAN_DAYS} hari`);
      setCustom({ from: draft.from, to: draft.to });
    }
    setApplied((a) => ({ search: draft.search.trim(), department: draft.department, n: a.n + 1 }));
    setPage(0);
  };
  const reset = () => {
    const week = weekRange(todayKey());
    setMode("week");
    setAnchor(todayKey());
    setCustom(week);
    setDraft({ search: "", department: "", ...week });
    setApplied((a) => ({ search: "", department: "", n: a.n + 1 }));
    setPage(0);
  };

  const handleDeleted = () => {
    closeDetail();
    load();
  };

  const today = todayKey();
  // Mode ringkas (bulan / rentang > 7 hari): kolom sempit supaya lebih banyak hari kelihatan sekaligus.
  const compact = days.length > 7;
  const gridCols = compact
    ? `236px repeat(${days.length}, minmax(64px, 1fr))`
    : `260px repeat(${days.length}, minmax(150px, 1fr))`;

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Absensi"
        subtitle="Kalender absensi karyawan — klik record untuk lihat detail (foto, lokasi, jam)."
        action={
          <div className="flex items-center gap-2">
            {toggle}
            <Button variant="secondary" size="sm" onClick={load}>
              <Icon.Refresh size={15} /> Refresh
            </Button>
          </div>
        }
      />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2.5 mb-3 shrink-0">
        <div className={`${pill} flex items-center gap-2 px-3.5 flex-1 min-w-[200px] max-w-xs`}>
          <Icon.Search size={16} className="text-slate-400" />
          <input
            value={draft.search}
            onChange={setDraftField("search")}
            onKeyDown={(e) => e.key === "Enter" && apply()}
            placeholder="Cari nama / NIK…"
            className="flex-1 min-w-0 bg-transparent text-sm focus:outline-none placeholder:text-slate-400"
          />
        </div>

        <label className={`${pill} flex items-center gap-2 pl-3.5 pr-2 cursor-pointer`}>
          <Icon.Filter size={16} className="text-slate-400" />
          <select
            value={draft.department}
            onChange={setDraftField("department")}
            className="bg-transparent text-sm text-slate-700 focus:outline-none cursor-pointer max-w-[200px]"
          >
            <option value="">Semua Departemen</option>
            {departments.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </label>

        <div className={`${pill} flex items-center p-1`}>
          {[
            ["week", "Minggu Ini"],
            ["month", "Bulan Ini"],
            ["custom", "Rentang"],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => changeMode(k)}
              className={`h-8 px-3.5 rounded-full text-[13px] font-medium transition ${
                mode === k ? "bg-brand-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-800"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className={`${pill} flex items-center gap-1 px-1`}>
          <button onClick={() => nav(-1)} className="h-8 w-8 rounded-full inline-flex items-center justify-center text-slate-500 hover:bg-slate-100" title="Sebelumnya">
            <Icon.ChevronLeft size={18} />
          </button>
          {mode === "custom" ? (
            <div className="flex items-center gap-1.5 px-1">
              <input type="date" value={draft.from} onChange={setDraftField("from")} className="text-[13px] font-medium text-slate-700 bg-transparent focus:outline-none" />
              <span className="text-slate-300">–</span>
              <input type="date" value={draft.to} onChange={setDraftField("to")} className="text-[13px] font-medium text-slate-700 bg-transparent focus:outline-none" />
            </div>
          ) : (
            <span className="flex items-center gap-2 px-2 text-[13px] font-semibold text-slate-700 whitespace-nowrap">
              {rangeLabel(range)}
              <Icon.Calendar size={15} className="text-slate-400" />
            </span>
          )}
          <button onClick={() => nav(1)} className="h-8 w-8 rounded-full inline-flex items-center justify-center text-slate-500 hover:bg-slate-100" title="Berikutnya">
            <Icon.ChevronRight size={18} />
          </button>
        </div>

        <button
          onClick={apply}
          className="h-10 px-5 rounded-full bg-brand-600 text-white text-[13px] font-semibold inline-flex items-center gap-1.5 shadow-sm hover:bg-brand-700 transition"
        >
          <Icon.Search size={15} /> Cari
        </button>

        {(mode !== "week" || anchor !== today || applied.department || applied.search || draft.department || draft.search) && (
          <button
            onClick={reset}
            className="text-[13px] font-medium text-slate-500 hover:text-brand-700 px-2"
          >
            Reset
          </button>
        )}
      </div>

      {/* Grid kalender */}
      <div className="flex-1 min-h-0 flex flex-col">
        {loading ? (
          <Loading />
        ) : error ? (
          <ErrorState message={error} onRetry={load} />
        ) : (
          <>
            <div className="flex-1 min-h-0 overflow-auto rounded-2xl">
              <div className="grid gap-y-2 min-w-max pb-2" style={{ gridTemplateColumns: gridCols }}>
                {/* Header */}
                <div className="sticky top-0 left-0 z-30 bg-appbg py-2.5 pl-2 text-[11px] font-semibold tracking-wider text-slate-400 uppercase">
                  Karyawan & Jabatan
                </div>
                {days.map((d) => {
                  const isToday = d === today;
                  if (compact) {
                    return (
                      <div key={d} className="sticky top-0 z-20 bg-appbg py-1 px-0.5 flex justify-center">
                        <span
                          className={`w-11 py-1 rounded-xl flex flex-col items-center leading-tight ${
                            isToday ? "bg-brand-600 text-white" : isWeekend(d) ? "text-slate-300" : "text-slate-400"
                          }`}
                        >
                          <span className="text-[9px] font-semibold uppercase tracking-wider">{fmt(d, { weekday: "short" })}</span>
                          <span className="text-[13px] font-bold">{fmt(d, { day: "numeric" })}</span>
                        </span>
                      </div>
                    );
                  }
                  return (
                    <div key={d} className="sticky top-0 z-20 bg-appbg py-1.5 px-1 flex justify-center">
                      <span
                        className={`px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wider uppercase whitespace-nowrap ${
                          isToday ? "bg-brand-600 text-white" : "text-slate-400"
                        }`}
                      >
                        {fmt(d, { day: "numeric", month: "short" })} ({fmt(d, { weekday: "short" })})
                      </span>
                    </div>
                  );
                })}

                {data.employees.length === 0 ? (
                  <div className="col-span-full bg-white rounded-2xl py-14 text-center text-sm text-slate-400">
                    Tidak ada karyawan untuk filter ini.
                  </div>
                ) : (
                  data.employees.map((emp) => (
                    <EmployeeRow
                      key={emp.user_id}
                      emp={emp}
                      days={days}
                      today={today}
                      recMap={recMap}
                      leaveSet={leaveSet}
                      onOpen={openDetail}
                      compact={compact}
                    />
                  ))
                )}
              </div>
            </div>
            <Legend />
            <Pagination page={page} pageSize={PAGE_SIZE} total={data.total} onPage={setPage} />
          </>
        )}
      </div>

      <AttendanceViewModal open={!!selected} row={selected} detail={detail} onClose={closeDetail} onDeleted={handleDeleted} />
    </div>
  );
}

function EmployeeRow({ emp, days, today, recMap, leaveSet, onOpen, compact }) {
  const present = days.reduce((n, d) => n + (recMap.has(`${emp.user_id}|${d}`) ? 1 : 0), 0);
  return (
    <>
      {/* Kartu karyawan (menempel kiri saat scroll horizontal) */}
      <div className="sticky left-0 z-10 bg-appbg pr-2">
        <div className={`h-full bg-white rounded-2xl border border-slate-200/70 flex items-center ${compact ? "px-2.5 py-1.5 gap-2" : "px-3 py-2.5 gap-2.5"}`}>
          <div className={`rounded-full bg-brand-100 text-brand-700 flex items-center justify-center font-bold shrink-0 ${compact ? "h-7 w-7 text-[10px]" : "h-9 w-9 text-xs"}`}>
            {initials(emp.employee_name)}
          </div>
          <div className="min-w-0">
            {/* Nama boleh turun baris (tidak dipotong) */}
            <div className="text-[11.5px] font-semibold text-slate-800 leading-snug break-words">{emp.employee_name}</div>
            <div className="text-[10px] text-slate-400 truncate" title={emp.job_title || emp.department || ""}>
              {emp.job_title || emp.department || emp.nik || "-"}
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5">
              <span className="font-semibold text-brand-700">{present}</span>/{days.length} hari tercatat
            </div>
          </div>
        </div>
      </div>

      {/* Sel per hari (subgrid → sejajar dengan header) */}
      <div
        className="grid bg-white rounded-2xl border border-slate-200/70"
        style={{ gridColumn: "2 / -1", gridTemplateColumns: "subgrid" }}
      >
        {days.map((d, i) => (
          <div
            key={d}
            className={`flex flex-col justify-center ${compact ? "p-1 gap-0.5 min-h-[46px]" : "p-1.5 gap-1 min-h-[58px]"} ${
              i > 0 ? "border-l border-dashed border-slate-200" : ""
            } ${d === today ? "bg-brand-50/40" : compact && isWeekend(d) ? "bg-slate-50/70" : ""}`}
          >
            <DayCell recs={recMap.get(`${emp.user_id}|${d}`)} onLeave={leaveSet.has(`${emp.user_id}|${d}`)} date={d} today={today} onOpen={onOpen} compact={compact} />
          </div>
        ))}
      </div>
    </>
  );
}

function DayCell({ recs, onLeave, date, today, onOpen, compact }) {
  if (recs?.length) {
    return recs.map((r) => {
      const c = CHIP[attendanceFlagColor(r.status)] || CHIP.gray;
      const review = r.flags?.needs_hr_review && r.flags?.review_status !== "resolved";
      const label = STATUS_LABEL[r.status] || r.status;
      if (compact) {
        // Ringkas: warna = status, isi = jam masuk/keluar; label lengkap di tooltip.
        return (
          <button
            key={r.id}
            onClick={() => onOpen(r)}
            title={`${label} · ${formatTime(r.check_in)} – ${r.check_out ? formatTime(r.check_out) : "…"}${review ? " · Perlu review HR" : ""}`}
            className={`relative w-full rounded-lg ${c.bg} py-1 flex flex-col items-center leading-tight transition hover:ring-2 hover:ring-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-400`}
          >
            <span className={`text-[10px] font-bold ${c.text}`}>{formatTime(r.check_in)}</span>
            <span className="text-[10px] text-slate-500">{r.check_out ? formatTime(r.check_out) : "…"}</span>
            {review && <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-rose-500" />}
          </button>
        );
      }
      return (
        <button
          key={r.id}
          onClick={() => onOpen(r)}
          title="Lihat detail absen"
          className={`relative w-full rounded-xl ${c.bg} px-2 py-1.5 text-left transition hover:ring-2 hover:ring-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-400`}
        >
          <div className="flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-full shrink-0 ${c.dot}`} />
            <span className={`text-[11px] font-bold truncate ${c.text}`}>{label}</span>
            {review && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-rose-500 shrink-0" title="Perlu review HR" />}
          </div>
          <div className="text-[11px] text-slate-600 mt-0.5 pl-3.5 whitespace-nowrap">
            {formatTime(r.check_in)} – {r.check_out ? formatTime(r.check_out) : "…"}
          </div>
        </button>
      );
    });
  }
  if (onLeave) {
    const c = CHIP.blue;
    return (
      <div className={`${c.bg} flex items-center ${compact ? "rounded-lg py-2 justify-center" : "rounded-xl px-2 py-1.5 gap-1.5"}`}>
        {!compact && <span className={`h-2 w-2 rounded-full ${c.dot}`} />}
        <span className={`font-bold ${c.text} ${compact ? "text-[10px]" : "text-[11px]"}`}>Cuti</span>
      </div>
    );
  }
  // Hari kerja (Sen–Jum) yang sudah lewat tanpa absen → tandai "Absen" (samar).
  if (date < today && !isWeekend(date)) {
    return (
      <div
        className={`border border-dashed border-[#f0c4cb] font-semibold text-[#c0737f] text-center ${
          compact ? "rounded-lg py-2 text-[10px]" : "rounded-xl px-2 py-1.5 text-[11px]"
        }`}
      >
        Absen
      </div>
    );
  }
  return (
    <div className={`bg-slate-50 flex items-center justify-center text-slate-300 text-xs ${compact ? "rounded-lg h-[34px]" : "rounded-xl h-[42px]"}`}>
      —
    </div>
  );
}

function Legend() {
  const items = [
    ["green", "Hadir"],
    ["yellow", "Telat / Half Day / Pulang Awal"],
    ["blue", "WFH / Cuti"],
    ["red", "Anomali"],
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[11px] text-slate-500">
      {items.map(([c, label]) => (
        <span key={c} className="inline-flex items-center gap-1.5">
          <span className={`h-2 w-2 rounded-full ${CHIP[c].dot}`} />
          {label}
        </span>
      ))}
      <span className="inline-flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-rose-500" /> Perlu review HR
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="h-3 w-5 rounded border border-dashed border-[#f0c4cb]" /> Tidak ada absen (hari kerja)
      </span>
    </div>
  );
}
